import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Newspaper, CheckCircle2, Mail } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  bcBriefSegments, bcBriefSubscribe, bcBriefConfirm,
  bcBriefGetPrefs, bcBriefUpdatePrefs,
} from "@/lib/api";

export default function BusinessCenterBrief() {
  const [params] = useSearchParams();
  const [segments, setSegments] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", industry: "", business_size: "small", consent: false, segments: ["general"] });
  const [submitted, setSubmitted] = useState(null);
  const [confirmed, setConfirmed] = useState(null);
  const [prefsState, setPrefsState] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const confirmToken = params.get("token");
  const unsubToken = params.get("unsub") || params.get("preferences");

  useEffect(() => {
    document.title = "Weekly AI Business Brief | WebsiteTech.pro";
    bcBriefSegments().then((d) => setSegments(d.items || []));
    if (confirmToken) {
      bcBriefConfirm(confirmToken).then(setConfirmed).catch((e) => setError(e?.response?.data?.detail || "Invalid confirmation link."));
    }
    if (unsubToken) {
      bcBriefGetPrefs(unsubToken).then(setPrefsState).catch((e) => setError(e?.response?.data?.detail || "Invalid preferences link."));
    }
  }, [confirmToken, unsubToken]);

  const toggleSeg = (key) => {
    setForm((f) => ({ ...f, segments: f.segments.includes(key) ? f.segments.filter((s) => s !== key) : [...f.segments, key] }));
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const r = await bcBriefSubscribe(form);
      setSubmitted(r);
    } catch (err) {
      setError(err?.response?.data?.detail || "Subscribe failed.");
    } finally { setBusy(false); }
  };

  // Preferences center flow
  if (unsubToken && prefsState) {
    return <PreferencesCenter token={unsubToken} initial={prefsState} segments={segments} />;
  }

  // Confirmation flow
  if (confirmToken) {
    return (
      <Shell>
        <div className="mt-8 rounded-2xl bg-white border border-slate-200 p-8 text-center" data-testid="bc-brief-confirmed">
          {confirmed ? (
            <>
              <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
              <h2 className="mt-3 text-2xl font-bold text-slate-900">You're subscribed</h2>
              <p className="mt-2 text-slate-600">Welcome to the WebsiteTech AI Business Brief. Look out for the first edition soon.</p>
              {confirmed.unsub_token && (
                <Link to={`/business-center/brief?preferences=${confirmed.unsub_token}`} className="mt-4 inline-block text-xs text-slate-500 underline">Manage preferences</Link>
              )}
            </>
          ) : error ? (
            <div className="text-rose-700">{error}</div>
          ) : (
            <div className="text-slate-500">Confirming…</div>
          )}
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      {submitted ? (
        <div className="mt-8 rounded-2xl bg-white border border-slate-200 p-8 text-center" data-testid="bc-brief-submitted">
          <Mail className="w-10 h-10 text-indigo-600 mx-auto" />
          <h2 className="mt-3 text-2xl font-bold text-slate-900">Check your inbox</h2>
          <p className="mt-2 text-slate-600">We sent a confirmation link to <strong>{submitted.email}</strong>. Click it to complete your subscription.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 rounded-2xl bg-white border border-slate-200 p-6 md:p-8" data-testid="bc-brief-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Email">
              <input type="email" required value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                data-testid="bc-brief-email"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </Field>
            <Field label="Name (optional)">
              <input type="text" value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                data-testid="bc-brief-name"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </Field>
            <Field label="Industry (optional)">
              <input type="text" value={form.industry}
                onChange={(e) => setForm((f) => ({ ...f, industry: e.target.value }))}
                data-testid="bc-brief-industry"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </Field>
            <Field label="Business size">
              <select value={form.business_size} onChange={(e) => setForm((f) => ({ ...f, business_size: e.target.value }))}
                data-testid="bc-brief-size"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
                {["micro", "small", "medium", "enterprise"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>

          <div className="mt-6">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-600">Pick the editions you want</div>
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
              {segments.map((s) => {
                const checked = form.segments.includes(s.key);
                return (
                  <button type="button" key={s.key} onClick={() => toggleSeg(s.key)}
                    data-testid={`bc-brief-segment-${s.key}`}
                    className={`text-left rounded-xl border p-3 transition ${
                      checked ? "border-indigo-500 bg-indigo-50" : "border-slate-200 bg-white hover:border-indigo-300"
                    }`}>
                    <div className="flex items-center justify-between">
                      <div className="font-semibold text-sm text-slate-900">{s.label}</div>
                      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{s.cadence} · {s.send_day}</span>
                    </div>
                    <div className="mt-1 text-xs text-slate-600">{s.description}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-6 flex items-start gap-2">
            <input type="checkbox" checked={form.consent} onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
              data-testid="bc-brief-consent"
              className="mt-1 w-4 h-4 accent-indigo-600" required />
            <div className="text-xs text-slate-600">
              I agree to receive the Weekly AI Business Brief by email. I can unsubscribe at any time via the link in every edition.
              WebsiteTech will never sell my email — see our <Link to="/privacy" className="text-indigo-700 font-semibold">privacy policy</Link>.
            </div>
          </div>

          <div className="mt-6">
            <button type="submit" disabled={busy || !form.consent || !form.email || form.segments.length === 0}
              data-testid="bc-brief-submit"
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed px-6 py-3 text-white font-semibold transition">
              {busy ? "Subscribing…" : "Subscribe"} <ArrowRight className="w-4 h-4" />
            </button>
            {error && <div className="mt-2 text-sm text-rose-700">{error}</div>}
          </div>
        </form>
      )}
    </Shell>
  );
}

function PreferencesCenter({ token, initial, segments }) {
  const [state, setState] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const toggle = (key) => {
    setState((s) => ({ ...s, segments: (s.segments || []).includes(key) ? s.segments.filter((x) => x !== key) : [...(s.segments || []), key] }));
  };
  const save = async () => {
    setBusy(true); setNotice("");
    try {
      const r = await bcBriefUpdatePrefs({ unsub_token: token, segments: state.segments });
      setState((s) => ({ ...s, segments: r.segments || [], status: r.status }));
      setNotice(r.status === "unsubscribed" ? "You have been unsubscribed from all editions." : "Preferences saved.");
    } catch (e) {
      setNotice(e?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };
  const unsubAll = async () => {
    setBusy(true); setNotice("");
    try {
      const r = await bcBriefUpdatePrefs({ unsub_token: token, unsubscribe_all: true });
      setState((s) => ({ ...s, segments: [], status: r.status }));
      setNotice("You've been unsubscribed from all editions.");
    } catch (e) {
      setNotice(e?.response?.data?.detail || "Unsubscribe failed.");
    } finally { setBusy(false); }
  };
  return (
    <Shell>
      <div className="mt-8 rounded-2xl bg-white border border-slate-200 p-6 md:p-8" data-testid="bc-brief-prefs">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Preferences for</div>
        <div className="text-lg font-bold text-slate-900">{state.email}</div>
        <div className={`mt-2 inline-block text-[10px] uppercase tracking-widest font-bold rounded px-2 py-0.5 ${
          state.status === "verified" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
          state.status === "unsubscribed" ? "bg-slate-100 text-slate-700 border border-slate-200" :
          "bg-amber-50 text-amber-800 border border-amber-200"
        }`}>{state.status}</div>

        <div className="mt-6">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-600">Editions you receive</div>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
            {segments.map((s) => {
              const checked = (state.segments || []).includes(s.key);
              return (
                <button type="button" key={s.key} onClick={() => toggle(s.key)}
                  data-testid={`bc-brief-pref-${s.key}`}
                  className={`text-left rounded-xl border p-3 transition ${
                    checked ? "border-indigo-500 bg-indigo-50" : "border-slate-200 bg-white hover:border-indigo-300"
                  }`}>
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-sm text-slate-900">{s.label}</div>
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{s.cadence}</span>
                  </div>
                  <div className="mt-1 text-xs text-slate-600">{s.description}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <button onClick={save} disabled={busy}
            data-testid="bc-brief-prefs-save"
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 px-5 py-2.5 text-white font-semibold text-sm transition">
            Save preferences
          </button>
          <button onClick={unsubAll} disabled={busy}
            data-testid="bc-brief-prefs-unsub-all"
            className="inline-flex items-center gap-2 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 px-5 py-2.5 font-semibold text-sm transition">
            Unsubscribe from all
          </button>
        </div>
        {notice && <div className="mt-3 text-sm text-slate-700">{notice}</div>}
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 pt-24 pb-16" data-testid="bc-brief">
        <div className="max-w-4xl mx-auto px-4">
          <div className="mb-3 text-xs uppercase tracking-widest text-indigo-700 font-bold">
            <Link to="/business-center" className="hover:text-indigo-900 inline-flex items-center gap-1"><ArrowLeft size={12} /> Business Center</Link>
          </div>
          <div className="flex items-center gap-3">
            <Newspaper className="w-8 h-8 text-indigo-600" />
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900">Weekly AI Business Brief</h1>
          </div>
          <p className="mt-2 text-slate-600 max-w-2xl">One development, one product, one automation, one action — every week. Pick industry-specific editions and unsubscribe with one click, anytime.</p>
          {children}
        </div>
      </main>
      <Footer />
    </div>
  );
}
function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1">{label}</div>
      {children}
    </label>
  );
}
